/* Created by Interactive 63 - https://interactive63.com */
/**
 * Animated words. CSS cannot change what a text element says, so each distinct wording becomes
 * its own span inside the element, and a stepped font-size animation shows exactly one of them at
 * a time: 1em for the current words, 0 for the rest. Zero-size glyphs take up no room, and line
 * offsets are in em, so hidden lines collapse too. This keeps word changes plain CSS, so they
 * play in every export, the preview and rendered video frames without the runtime.
 */
import { easingToCss } from '../easing/to-css';
import type { ElementId, Project, PropertyValue, Track } from '../model/types';
import { hasNumberedChildren, lineStartX, TEXT_TAGS, writeTextContent } from '../text/text';
import { effectiveKeyframes } from '../timeline/repeat';
import { sampleTrack } from '../timeline/sample';
import { isExcluded } from './animated';
import { percent, trackTiming, type CssScene } from './css';
import type { CompileTarget, CompileWarning, SceneTiming } from './types';

const SHOWN = '1em';
const HIDDEN = '0px';
const SVG_NS = 'http://www.w3.org/2000/svg';

export interface WordsBuild {
  elementId: ElementId;
  track: Track;
  /** Each distinct wording, in the order the keyframes first use it. */
  variants: string[];
}

/** The words tracks that will be compiled, each with its distinct wordings. */
export function collectWords(project: Project, target: CompileTarget): WordsBuild[] {
  const builds: WordsBuild[] = [];
  for (const track of project.tracks) {
    if (!track.enabled || track.property !== 'words' || track.keyframes.length === 0) continue;
    const meta = project.elements[track.elementId];
    if (!meta || !TEXT_TAGS.has(meta.tag)) continue;
    if (target === 'export' && isExcluded(project, track.elementId)) continue;
    const variants: string[] = [];
    for (const kf of track.keyframes) {
      if (typeof kf.value === 'string' && !variants.includes(kf.value)) variants.push(kf.value);
    }
    if (variants.length > 0) builds.push({ elementId: track.elementId, track, variants });
  }
  return builds;
}

/** The class of one wording's span. */
function variantClass(id: ElementId, index: number): string {
  return `st-${id}-w${index}`;
}

/**
 * Rewrites each text element's content for its words track: constant words replace the content,
 * changing words become one span per wording. Text made of numbered spans is left alone.
 */
export function applyWordsMarkup(
  byId: ReadonlyMap<string, Element>,
  builds: readonly WordsBuild[],
  labelOf: (id: ElementId) => string,
): CompileWarning[] {
  const warnings: CompileWarning[] = [];
  for (const build of builds) {
    const el = byId.get(build.elementId);
    if (!el) continue;
    if (hasNumberedChildren(el)) {
      warnings.push({
        code: 'words-spans',
        message: `${labelOf(build.elementId)} is made of separate spans, so its words cannot change. Animate the words of each span instead.`,
        elementId: build.elementId,
      });
      continue;
    }
    const x = lineStartX(el);
    const [only] = build.variants;
    if (build.variants.length === 1 && only !== undefined) {
      writeTextContent(el, only, x);
      continue;
    }
    el.replaceChildren();
    build.variants.forEach((words, index) => {
      const span = el.ownerDocument.createElementNS(SVG_NS, 'tspan');
      span.setAttribute('class', variantClass(build.elementId, index));
      writeTextContent(span, words, x);
      el.append(span);
    });
  }
  return warnings;
}

/** The font size that shows or hides one wording for a keyframe value. */
function sizeFor(value: PropertyValue | undefined, words: string): string {
  return value === words ? SHOWN : HIDDEN;
}

/** Whether a build switches between wordings over time. */
function changes(build: WordsBuild): boolean {
  return build.variants.length > 1 && build.track.keyframes.length > 1;
}

/** One rule plus one stepped @keyframes block per wording of each changing words track. */
export function wordsCss(
  builds: readonly WordsBuild[],
  timing: SceneTiming,
  scene: CssScene,
): { css: string; selectors: string[] } {
  const hold = easingToCss({ type: 'hold' }).value;
  const sections: string[] = [];
  const selectors: string[] = [];
  for (const build of builds) {
    if (!changes(build)) continue;
    const { track, elementId } = build;
    const keyframes = effectiveKeyframes(track, timing.duration).keyframes;
    const first = keyframes[0];
    const last = keyframes[keyframes.length - 1];
    if (!first || !last) continue;
    build.variants.forEach((words, index) => {
      const name = `st-${scene.sceneName}-${elementId}-words-${index}`;
      const selector = `.${scene.sceneClass} .${variantClass(elementId, index)}`;
      const steps: string[] = [];
      if (first.time > 0) steps.push(`  0% { font-size: ${sizeFor(first.value, words)}; }`);
      keyframes.forEach((kf, at) => {
        const easing = at < keyframes.length - 1 ? ` animation-timing-function: ${hold};` : '';
        steps.push(
          `  ${percent(kf.time, timing.duration)} { font-size: ${sizeFor(kf.value, words)};${easing} }`,
        );
      });
      if (last.time < timing.duration) {
        steps.push(`  100% { font-size: ${sizeFor(last.value, words)}; }`);
      }
      selectors.push(selector);
      sections.push(
        `${selector} {\n  animation: ${name} ${trackTiming(track, timing)};\n  font-size: ${sizeFor(first.value, words)};\n}`,
        `@keyframes ${name} {\n${steps.join('\n')}\n}`,
      );
    });
  }
  return { css: sections.length > 0 ? `${sections.join('\n\n')}\n` : '', selectors };
}

/** Reduced-motion rules that pin each wording to the first or final words. */
export function wordsReducedMotionRules(
  builds: readonly WordsBuild[],
  scene: CssScene,
  at: 'initial' | 'final',
  duration: number,
): string[] {
  const rules: string[] = [];
  for (const build of builds) {
    if (!changes(build)) continue;
    const value = sampleTrack(build.track, at === 'final' ? duration : 0, duration);
    build.variants.forEach((words, index) => {
      rules.push(
        `.${scene.sceneClass} .${variantClass(build.elementId, index)} {\n  animation: none !important;\n  font-size: ${sizeFor(value, words)};\n}`,
      );
    });
  }
  return rules;
}
